import { Failure, Ok, Result } from "@megaron/result";

import { ShopifyAdminClient } from "./client";
import { OrderCancelReason } from "./generated/admin.types";

const orderCancelReasonFromApi: Record<
  "CUSTOMER" | "DECLINED" | "FRAUD" | "INVENTORY" | "STAFF" | "OTHER",
  OrderCancelReason
> = {
  CUSTOMER: OrderCancelReason.Customer,
  DECLINED: OrderCancelReason.Declined,
  FRAUD: OrderCancelReason.Fraud,
  INVENTORY: OrderCancelReason.Inventory,
  STAFF: OrderCancelReason.Staff,
  OTHER: OrderCancelReason.Other,
};

export type RunOrderCancelInput = {
  orderLegacyId: string;
  notifyCustomer: boolean;
  restock: boolean;
  refundOriginalPayment: boolean;
  reason: keyof typeof orderCancelReasonFromApi;
  staffNote: string | null;
};

export const orderCancelGql = `#graphql
  mutation OrderCancel(
    $orderId: ID!
    $notifyCustomer: Boolean
    $refundMethod: OrderCancelRefundMethodInput!
    $restock: Boolean!
    $reason: OrderCancelReason!
    $staffNote: String
  ) {
    orderCancel(
      orderId: $orderId
      notifyCustomer: $notifyCustomer
      refundMethod: $refundMethod
      restock: $restock
      reason: $reason
      staffNote: $staffNote
    ) {
      job {
        id
      }
      orderCancelUserErrors {
        field
        message
        code
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const readOrderCancelErrors = (payload: unknown): string | null => {
  if (!isRecord(payload)) return null;
  const primary = payload["orderCancelUserErrors"];
  const legacy = payload["userErrors"];
  const collect = (arr: unknown): string[] => {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    const parts: string[] = [];
    for (const e of arr) {
      if (!isRecord(e)) continue;
      const msg = e["message"];
      if (typeof msg === "string") parts.push(msg);
    }
    return parts;
  };
  const a = collect(primary);
  const b = collect(legacy);
  const merged = [...a, ...b];
  return merged.length ? merged.join("; ") : null;
};

const toOrderGid = (legacyId: string): string => `gid://shopify/Order/${legacyId}`;

export const runOrderCancel = async (input: RunOrderCancelInput): Promise<Result<void, string>> => {
  const client = ShopifyAdminClient();
  const res = await client.request(orderCancelGql, {
    variables: {
      orderId: toOrderGid(input.orderLegacyId),
      notifyCustomer: input.notifyCustomer,
      refundMethod: {
        originalPaymentMethodsRefund: input.refundOriginalPayment,
      },
      restock: input.restock,
      reason: orderCancelReasonFromApi[input.reason],
      staffNote: input.staffNote ?? undefined,
    },
  });

  const data = res?.data;
  if (data === undefined || data === null) return Failure("ShopifyOrderCancelNoData");

  const payload = isRecord(data) ? data["orderCancel"] : undefined;
  if (!isRecord(payload)) return Failure("ShopifyOrderCancelFailed");

  const errMsg = readOrderCancelErrors(payload);
  if (errMsg !== null) return Failure(errMsg);

  return Ok();
};
